import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReviewFeedback, useSubmitReviewFeedback } from '../hooks/useApi';
import { MessageSquare, ChevronDown, ChevronUp, Send, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ReviewFeedbackData } from '../types';

type Accuracy = 'accurate' | 'partial' | 'inaccurate';
type ScoreAssessment = 'too_high' | 'about_right' | 'too_low';

const ISSUE_OPTIONS = [
  { value: 'missed_key_skills', label: 'Missed key skills' },
  { value: 'wrong_experience', label: 'Wrong experience assessment' },
  { value: 'score_mismatch', label: "Score doesn't match quality" },
  { value: 'missing_context', label: 'Missing context' },
  { value: 'screening_conflict', label: 'Screening conflict' },
  { value: 'other', label: 'Other' },
];

const ACCURACY_OPTIONS = [
  { value: 'accurate' as Accuracy, label: 'Accurate', color: 'green' },
  { value: 'partial' as Accuracy, label: 'Partially', color: 'amber' },
  { value: 'inaccurate' as Accuracy, label: 'Inaccurate', color: 'red' },
] as const;

interface InlineReviewFeedbackProps {
  reviewId: number;
  applicationId: number;
  navigateOnSubmit?: boolean;
}

export function InlineReviewFeedback({ reviewId, applicationId, navigateOnSubmit = true }: InlineReviewFeedbackProps) {
  const navigate = useNavigate();
  const { data: feedbackData, isLoading } = useReviewFeedback(reviewId);
  const submitMutation = useSubmitReviewFeedback();

  const [expanded, setExpanded] = useState(false);
  const [accuracy, setAccuracy] = useState<Accuracy | null>(null);
  const [scoreAssessment, setScoreAssessment] = useState<ScoreAssessment | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [comments, setComments] = useState('');
  const [requestReview, setRequestReview] = useState(false);
  const [reviewGuidance, setReviewGuidance] = useState('');
  const [showPrevious, setShowPrevious] = useState(false);

  const feedbackCount = feedbackData?.data?.length ?? 0;

  const resetForm = useCallback(() => {
    setAccuracy(null);
    setScoreAssessment(null);
    setIssues([]);
    setComments('');
    setRequestReview(false);
    setReviewGuidance('');
    setExpanded(false);
  }, []);

  const handleAccuracyClick = (value: Accuracy) => {
    setAccuracy(value);
    setExpanded(true);
    if (value === 'accurate') {
      setScoreAssessment(null);
      setIssues([]);
      setRequestReview(false);
      setReviewGuidance('');
    }
  };

  const toggleIssue = (value: string) => {
    setIssues((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value],
    );
  };

  const handleSubmit = async () => {
    if (!accuracy) return;

    const data: ReviewFeedbackData = { accuracy };
    if (scoreAssessment) data.scoreAssessment = scoreAssessment;
    if (issues.length > 0) data.issues = issues;
    if (comments.trim()) data.comments = comments.trim();
    if (requestReview) {
      data.requestReview = true;
      if (reviewGuidance.trim()) data.reviewGuidance = reviewGuidance.trim();
    }

    await submitMutation.mutateAsync({ reviewId, data });
    resetForm();
    // Navigate to the application page, highlighting the feedback (unless suppressed)
    if (navigateOnSubmit && applicationId) {
      navigate(`/applications/${applicationId}?reviewId=${reviewId}&feedbackSubmitted=true`);
    }
  };

  const showDetailedFeedback = accuracy === 'partial' || accuracy === 'inaccurate';

  const colorMap: Record<string, { bg: string; border: string; text: string; dot: string }> = {
    green: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-500 dark:border-emerald-400',
      text: 'text-emerald-700 dark:text-emerald-300',
      dot: 'bg-emerald-500',
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-500 dark:border-amber-400',
      text: 'text-amber-700 dark:text-amber-300',
      dot: 'bg-amber-500',
    },
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-500 dark:border-red-400',
      text: 'text-red-700 dark:text-red-300',
      dot: 'bg-red-500',
    },
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
      {/* Collapsed: rating buttons row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Rate this review:</span>
        {ACCURACY_OPTIONS.map((opt) => {
          const isSelected = accuracy === opt.value;
          const colors = colorMap[opt.color];
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleAccuracyClick(opt.value)}
              className={`
                px-2.5 py-1 rounded-md text-xs font-medium border transition-all
                ${isSelected
                  ? `${colors.bg} ${colors.border} ${colors.text} ring-1 ring-current`
                  : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                }
              `}
            >
              {opt.label}
            </button>
          );
        })}
        {feedbackCount > 0 && !isLoading && (
          <button
            onClick={() => setShowPrevious(!showPrevious)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
          >
            <MessageSquare className="h-3 w-3" />
            {feedbackCount} feedback
          </button>
        )}
      </div>

      {/* Expanded form */}
      {expanded && accuracy && (
        <div className="mt-3 space-y-3 animate-slide-down">
          {/* Score Assessment */}
          {showDetailedFeedback && (
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">How was the score?</label>
              <div className="flex flex-wrap gap-2">
                {([
                  { value: 'too_high' as ScoreAssessment, label: 'Too High' },
                  { value: 'about_right' as ScoreAssessment, label: 'About Right' },
                  { value: 'too_low' as ScoreAssessment, label: 'Too Low' },
                ]).map((opt) => {
                  const isSelected = scoreAssessment === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setScoreAssessment(isSelected ? null : opt.value)}
                      className={`
                        px-2.5 py-1 rounded-md text-xs font-medium border transition-all
                        ${isSelected
                          ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-400 ring-1 ring-primary-500'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Issues checklist */}
          {showDetailedFeedback && (
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 block">What was wrong?</label>
              <div className="grid grid-cols-2 gap-1.5">
                {ISSUE_OPTIONS.map((opt) => {
                  const isChecked = issues.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`
                        flex items-center gap-1.5 px-2 py-1.5 rounded border cursor-pointer text-xs transition-all
                        ${isChecked
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleIssue(opt.value)}
                        className="h-3 w-3 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                      />
                      <span className="text-gray-700 dark:text-gray-300">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comments */}
          <div>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              Comments <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Additional feedback..."
              rows={2}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-vertical"
            />
          </div>

          {/* Request re-review */}
          {accuracy === 'inaccurate' && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requestReview}
                  onChange={(e) => setRequestReview(e.target.checked)}
                  className="h-3.5 w-3.5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Request an updated review
                </span>
              </label>
              {requestReview && (
                <textarea
                  value={reviewGuidance}
                  onChange={(e) => setReviewGuidance(e.target.value)}
                  placeholder="What should the updated review focus on?"
                  rows={2}
                  className="mt-1.5 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-vertical"
                />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitMutation.isPending}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="h-3 w-3" />
              {submitMutation.isPending ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-3 w-3" />
              Cancel
            </button>
            {submitMutation.isError && (
              <span className="text-xs text-red-600 dark:text-red-400">
                Failed to submit. Please try again.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Previous feedback entries */}
      {showPrevious && feedbackData?.data && feedbackData.data.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Previous Feedback ({feedbackData.data.length})
            </span>
            <button
              onClick={() => setShowPrevious(false)}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
          </div>
          {feedbackData.data.map((entry) => {
            const raw = entry.rawContent;
            const accuracyLabel = raw?.accuracy
              ? { accurate: 'Accurate', partial: 'Partially Accurate', inaccurate: 'Inaccurate' }[raw.accuracy]
              : null;
            const accuracyColor = raw?.accuracy
              ? { accurate: 'text-emerald-600 dark:text-emerald-400', partial: 'text-amber-600 dark:text-amber-400', inaccurate: 'text-red-600 dark:text-red-400' }[raw.accuracy]
              : '';

            return (
              <div
                key={entry.id}
                className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-xs"
              >
                <div className="flex items-center justify-between mb-1">
                  {accuracyLabel && (
                    <span className={`font-medium ${accuracyColor}`}>{accuracyLabel}</span>
                  )}
                  <span className="text-gray-400 dark:text-gray-500">
                    {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                    {entry.submitterId && entry.submitterId !== 'anonymous' && (
                      <> &middot; {entry.submitterId}</>
                    )}
                  </span>
                </div>
                {raw?.comments && (
                  <p className="text-gray-600 dark:text-gray-400 mt-0.5">{raw.comments}</p>
                )}
                {raw?.issues && raw.issues.length > 0 && (
                  <p className="text-gray-500 dark:text-gray-500 mt-0.5">
                    Issues: {raw.issues.join(', ')}
                  </p>
                )}
                {raw?.requestReview && (
                  <p className="text-primary-600 dark:text-primary-400 mt-0.5">
                    Re-review requested
                    {raw.reviewGuidance && `: ${raw.reviewGuidance}`}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
